/**
 * @fileoverview Store Pinia per la gestione dello stato generale dell'applicazione
 *
 * Migrazione da store/app.ts (Vuex) a stores/app.ts (Pinia)
 * Mantiene la stessa interfaccia e funzionalità del modulo Vuex originale.
 *
 * @author Migrazione Vuex → Pinia
 * @since 2025-09-23
 */

import { defineStore } from 'pinia';

/**
 * Interfaccia per lo state del modulo app
 * (Identica a quella di Vuex per compatibilità)
 */
export interface AppState {
	/** Indica se la sidebar è aperta o chiusa */
	sidebarOpened: boolean;
	/** Metadati generali dell'applicazione */
	meta: any | null;
	/** Stato collapsed dei filtri per pagina (chiave: page-key, valore: isCollapsed) */
	filtersCollapsedState: Record<string, boolean>;
}

/**
 * Store Pinia per la gestione dello stato generale dell'applicazione
 *
 * **Caratteristiche:**
 * - 🔄 **API compatibile** con il modulo Vuex esistente
 * - 🎯 **Stessi getter e actions** per facilità di migrazione
 * - 📱 **TypeScript** completamente tipizzato
 * - 🛡️ **Persistenza** configurata per sidebar state
 *
 * **Migrazione da Vuex:**
 * - `this.$store.getters['app/sidebarOpened']` → `useAppStore().isSidebarOpened`
 * - `this.$store.commit('app/TOGGLE_SIDEBAR')` → `useAppStore().toggleSidebar()`
 * - `this.$store.getters['app/meta']` → `useAppStore().meta` or `storeToRefs(useAppStore()).meta`
 * - `this.$store.commit('app/SET_META', value)` → `useAppStore().setMeta(value)`
 *
 * @example
 * ```typescript
 * // Usage in script setup:
 * import { useAppStore } from '@/stores/app'
 * import { storeToRefs } from 'pinia'
 *
 * const appStore = useAppStore()
 * const { sidebarOpened, meta } = storeToRefs(appStore)
 *
 * // Actions
 * appStore.toggleSidebar()
 * appStore.setMeta({ title: 'Dashboard' })
 * ```
 *
 * @example
 * ```typescript
 * // Usage in Options API:
 * import { useAppStore } from '@/stores/app'
 *
 * export default {
 *   computed: {
 *     isSidebarOpened() {
 *       return useAppStore().isSidebarOpened
 *     },
 *     appMeta() {
 *       return useAppStore().meta
 *     }
 *   },
 *   methods: {
 *     toggleSidebar() {
 *       useAppStore().toggleSidebar()
 *     }
 *   }
 * }
 * ```
 */
export const useAppStore = defineStore('app', {
	/**
	 * Stato iniziale del store
	 * (Identico al modulo Vuex)
	 */
	state: (): AppState => ({
		sidebarOpened: true,
		meta: null,
		filtersCollapsedState: {},
	}),

	/**
	 * Getters (computed properties reattive)
	 * Mantengono la stessa logica del modulo Vuex
	 */
	getters: {
		/**
		 * Indica se la sidebar è aperta
		 * @param state - State del store
		 * @returns True se la sidebar è aperta
		 */
		isSidebarOpened: (state): boolean => state.sidebarOpened,

		/**
		 * Ottiene lo stato collapsed dei filtri per una specifica pagina
		 * @param state - State del store
		 * @returns Funzione che accetta pageKey e ritorna lo stato collapsed
		 */
		isFiltersCollapsed:
			(state) =>
			(pageKey: string): boolean => {
				return state.filtersCollapsedState[pageKey] ?? false;
			},
	},

	/**
	 * Actions (metodi per modificare lo state)
	 * Sostituiscono le mutations di Vuex
	 */
	actions: {
		/**
		 * Toggle dello stato della sidebar
		 * Sostituisce la mutation TOGGLE_SIDEBAR
		 */
		toggleSidebar(): void {
			this.sidebarOpened = !this.sidebarOpened;
		},

		/**
		 * Imposta i metadati dell'applicazione
		 * Sostituisce la mutation SET_META
		 *
		 * @param value - Nuovi metadati da impostare
		 */
		setMeta(value: any): void {
			this.meta = value;
		},

		/**
		 * Imposta lo stato collapsed dei filtri per una pagina specifica
		 *
		 * @param pageKey - Identificatore univoco della pagina (es: 'timecards', 'workers')
		 * @param isCollapsed - True se i filtri sono compressi, false se espansi
		 */
		setFiltersCollapsed(pageKey: string, isCollapsed: boolean): void {
			// Forza la reattività creando un nuovo oggetto
			this.filtersCollapsedState = {
				...this.filtersCollapsedState,
				[pageKey]: isCollapsed,
			};
		},

		/**
		 * Toggle dello stato collapsed dei filtri per una pagina specifica
		 *
		 * @param pageKey - Identificatore univoco della pagina
		 */
		toggleFiltersCollapsed(pageKey: string): void {
			const currentState = this.filtersCollapsedState[pageKey] ?? false;
			this.filtersCollapsedState[pageKey] = !currentState;
		},
	},

	/**
	 * Configurazione persistenza con SecureLS
	 * Utilizza le stesse chiavi del setup Vuex per garantire compatibilità
	 */
	persist: {
		key: 'app',
		paths: ['sidebarOpened', 'filtersCollapsedState'], // Persistiamo anche lo stato dei filtri
	} as any, // Temporary type override per compatibilità
});
